import { SUPPORT_FORM_URL } from "@utils/constants";
import { isAuthorized } from "@utils/utils";
import React, { PropsWithChildren } from "react";
import { Navigate } from "react-router-dom";
import { roleType } from "src/layouts/config";

import { Button, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

interface AuthorizeProps extends PropsWithChildren {
  /** When true, unauthorized users are redirected to `to` (default "/") instead
   *  of seeing the 403 page. Use for routes where an unauthorized visit is a
   *  stale-bookmark case, not a deliberate attempt to access restricted content. */
  redirect?: boolean;
  to?: string;
  allowedRoles: roleType[];
}

export const NotAllowedPage: React.FC = () => {
  return (
    <div className="bg-inherit">
      <div className="flex flex-col items-center h-screen pt-16 bg-inherit">
        <Headline size="medium">403</Headline>
        <Headline size="medium" className="mt-4">
          Oops, you don't have permission to access this page.
        </Headline>

        <Paragraph displayType="normal" colorType="secondary" className="text-center my-4">
          You can return to the homepage, or if you think something is broken you may report a problem.
        </Paragraph>

        <div className="flex space-x-2 pt-6">
          <Button
            appearance="primary"
            text="Return to Homepage"
            className="px-6"
            onClick={() => (window.location.href = "/")}
          />
          <Button
            appearance="secondary"
            className="px-6"
            text="Report a Problem"
            onClick={() => window.open(SUPPORT_FORM_URL, "_blank", "noopener,noreferrer")}
          />
        </div>
      </div>
    </div>
  );
};

export const Authorize: React.FC<AuthorizeProps> = ({ children, allowedRoles = [], redirect, to }) => {
  const authorized = isAuthorized(allowedRoles);

  if (!authorized) {
    if (redirect) return <Navigate to={to ?? "/"} replace />;
    return <NotAllowedPage />;
  }
  return children;
};
